import "./style.scss";

import type { FilterOptionsState } from "@mui/material";
import { Avatar } from "@mui/material";
import Book from "assets/img/svg/navbar/book";
import Plus from "assets/img/svg/plus";
import AutocompleteFormComponent, {
    type AutocompleteFormComponentProps,
} from "components/shared/FormComponent/AutocompleteFormComponent";
import type { IWork } from "containers/Works/types";
import NewWorkModal from "containers/Works/NewWorkModal";
import { coverUrlForWork, workDisplayLabel } from "containers/Works/workPreview";
import { jaroWinkler } from "helpers/jaroWinkler";
import { useAbility } from "hooks/useAbility";
import { useDebounce } from "hooks/useDebounce";
import type { HTMLAttributes } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchWorkNames, fetchWorks, WORKS_PAGE_SIZE } from "store/workStore";

import variables from "../../../assets/styles/_variables.module.scss";

const ADD_WORK_ID = -1;
const WORK_NAME_MATCH_THRESHOLD = 0.9;
const WORK_NAME_DEBOUNCE_MS = 300;

type WorkName = Pick<IWork, "id" | "name" | "workCode">;

const findClosestWorkByName = (name: string, works: WorkName[]): WorkName | null => {
    const query = name.trim().toLowerCase();
    if (!query) return null;

    let closestWork: WorkName | null = null;
    let bestScore = 0;
    let bestScoreCount = 0;
    for (const work of works) {
        const score = jaroWinkler(query, work.name.trim().toLowerCase());
        if (score < WORK_NAME_MATCH_THRESHOLD) continue;
        if (score > bestScore) {
            bestScore = score;
            closestWork = work;
            bestScoreCount = 1;
        } else if (score === bestScore) {
            bestScoreCount += 1;
        }
    }
    return bestScoreCount > 1 ? null : closestWork;
};

type WorkOption = IWork & { addElement?: string };

const workOptionFromName = (option: WorkName): WorkOption => ({
    id: option.id,
    name: option.name,
    workCode: option.workCode ?? null,
    companyId: 0,
    artworkNature: null,
    analysisCode: null,
    associatedBrand: null,
    compensationGroupId: null,
    createdAt: "",
    updatedAt: "",
    titles: [],
});

type WorkSelectFieldProps<T extends FieldValues> = {
    control: Control<T>;
    error?: AutocompleteFormComponentProps["error"];
    required?: boolean;
    disabled?: boolean;
    extraOption?: IWork | null;
    allowCreate?: boolean;
    suggestedName?: string;
    onSelectWork?: (work: WorkName | null) => void;
};

const WorkSelectField = <T extends FieldValues>({
    control,
    error,
    required = true,
    disabled = false,
    extraOption = null,
    allowCreate = false,
    suggestedName = "",
    onSelectWork,
}: WorkSelectFieldProps<T>) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const ability = useAbility();
    const canCreateWork = allowCreate && ability.can("create", "Work");
    const selectedWork = useWatch({ control, name: "work" as Path<T> }) as WorkOption | null;
    const { works, worksStatus, countWorks } = useAppSelector((state) => state.works);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [workNames, setWorkNames] = useState<WorkName[] | null>(null);
    const workNamesRequest = useRef<Promise<WorkName[]> | null>(null);
    const options =
        extraOption != null && !works.some((work) => work.id === extraOption.id) ? [extraOption, ...works] : works;
    const trimmedSuggestion = suggestedName.trim();
    const debouncedSuggestion = useDebounce(trimmedSuggestion, WORK_NAME_DEBOUNCE_MS);
    const hasFinishedTyping = trimmedSuggestion === debouncedSuggestion;
    const hasNoSelectedWork = selectedWork == null || selectedWork.id < 1;
    const shouldMatchCatalog = !disabled && hasNoSelectedWork && hasFinishedTyping && debouncedSuggestion.length > 0;
    const closestWork = useMemo(
        () => (shouldMatchCatalog && workNames != null ? findClosestWorkByName(debouncedSuggestion, workNames) : null),
        [debouncedSuggestion, shouldMatchCatalog, workNames],
    );
    const catalogReady = shouldMatchCatalog && workNames != null;
    const showSelectSuggestion = catalogReady && closestWork != null;
    const showCreateSuggestion = catalogReady && canCreateWork && closestWork == null && debouncedSuggestion.length > 0;

    const loadWorkNames = () => {
        if (workNames != null || workNamesRequest.current != null) return;
        workNamesRequest.current = fetchWorkNames();
        workNamesRequest.current
            .then((names) => setWorkNames(names))
            .catch(() => {
                workNamesRequest.current = null;
            });
    };

    const loadWorks = () => {
        if (worksStatus === "idle") {
            dispatch(fetchWorks({ limit: WORKS_PAGE_SIZE, offset: 0 }));
        }
        loadWorkNames();
    };

    const loadMoreWorks = () => {
        if (worksStatus === "pending" || works.length >= countWorks) return;
        dispatch(fetchWorks({ limit: WORKS_PAGE_SIZE, offset: works.length }));
    };

    const openCreateModal = () => setIsCreateOpen(true);

    const workById = useMemo(() => new Map(options.map((work) => [work.id, work])), [options]);

    const keepAddWorkOption = (workOptions: WorkOption[], state: FilterOptionsState<WorkOption>) => {
        const addOption = workOptions.find((option) => option.id === ADD_WORK_ID);
        const query = state.inputValue.trim().toLowerCase();
        const catalog = query && workNames != null ? workNames : options;
        const filteredWorks = catalog
            .filter((option) => {
                if (option.id === ADD_WORK_ID) return false;
                if (!query) return true;
                const name = (option.name ?? "").toLowerCase();
                const workCode = ("workCode" in option ? option.workCode : "")?.toLowerCase() ?? "";
                return name.includes(query) || workCode.includes(query);
            })
            .map((option) => workById.get(option.id) ?? workOptionFromName(option));
        return addOption ? [...filteredWorks, addOption] : filteredWorks;
    };

    const renderOption = (props: HTMLAttributes<HTMLLIElement>, option: WorkOption, state: { selected: boolean }) => {
        if (option.id === ADD_WORK_ID || option.addElement) {
            return (
                <li {...props} key="add-work" className="autocomplete-new-element">
                    <div className="content">
                        <Plus />
                        {option.addElement ?? t("Works.NewWork")}
                    </div>
                </li>
            );
        }
        const coverUrl = coverUrlForWork(option);
        return (
            <li {...props} key={option.id} className={`autocomplete-beneficiaries${state.selected ? " selected" : ""}`}>
                <div className="autocomplete-beneficiaries-left-content">
                    <Avatar
                        className="autocomplete-avatar"
                        src={coverUrl}
                        variant="rounded"
                        sx={{
                            backgroundColor: "#EEF3FD",
                            color: variables["foreground"],
                            fontSize: "14px",
                            height: "40px !important",
                        }}
                    >
                        {(option.name ?? "").slice(0, 1)}
                    </Avatar>
                    <span>
                        {option.name ?? ""}
                        {option.workCode?.trim() ? (
                            <span className="work-select-field__code"> {option.workCode.trim()}</span>
                        ) : null}
                    </span>
                </div>
            </li>
        );
    };

    useEffect(() => {
        if (disabled || !hasNoSelectedWork || !debouncedSuggestion || workNames != null) return;

        loadWorkNames();
    }, [debouncedSuggestion, disabled, hasNoSelectedWork, workNames]);

    useEffect(() => {
        if (!canCreateWork || selectedWork?.id !== ADD_WORK_ID) return;
        onSelectWork?.(null);
        openCreateModal();
    }, [canCreateWork, onSelectWork, selectedWork]);

    return (
        <div className="work-select-field">
            <AutocompleteFormComponent
                label={t("Works.WorkLabel")}
                control={control}
                controlName="work"
                options={options}
                renderOptionText={(work: WorkOption | null) => workDisplayLabel(work)}
                renderOption={renderOption}
                isOptionEqualToValue={(option: IWork, value: IWork) => option.id === value.id}
                required={required}
                error={error}
                disabled={disabled}
                loading={worksStatus === "pending"}
                onOpen={loadWorks}
                onScrollEnd={loadMoreWorks}
                onScrollEndLoading={worksStatus}
                filterOptions={keepAddWorkOption}
                defaultFilterOptions={false}
                addElement={
                    canCreateWork && worksStatus !== "pending" && worksStatus !== "idle"
                        ? t("Works.NewWork")
                        : undefined
                }
                placeholder={t("NewTitle.SelectWork")}
                customRequiredMessage={t("ConnectTitle.RequiredWork")}
            />
            {showSelectSuggestion && closestWork && (
                <button
                    type="button"
                    key={`select-${closestWork.id}`}
                    className="work-select-field__suggestion"
                    onClick={() => onSelectWork?.(closestWork)}
                >
                    <Book />
                    <span>{t("Works.SelectWorkFromTitle", { name: workDisplayLabel(closestWork) })}</span>
                </button>
            )}
            {showCreateSuggestion && (
                <button
                    type="button"
                    key={`create-${debouncedSuggestion}`}
                    className="work-select-field__suggestion"
                    onClick={openCreateModal}
                >
                    <Plus />
                    <span>{t("Works.CreateWorkFromTitle", { name: debouncedSuggestion })}</span>
                </button>
            )}
            {canCreateWork && (
                <NewWorkModal
                    open={isCreateOpen}
                    initialName={trimmedSuggestion}
                    onClose={() => setIsCreateOpen(false)}
                    onCreated={(work) => {
                        setWorkNames((names) =>
                            names == null
                                ? names
                                : [...names, { id: work.id, name: work.name, workCode: work.workCode }],
                        );
                        onSelectWork?.(work);
                        setIsCreateOpen(false);
                    }}
                />
            )}
        </div>
    );
};

export default WorkSelectField;
